import { useState } from 'react';
import axiosInstance from 'interceptor/axiosInstance';
import { ApiRoutes } from 'routes/routeConstants/apiRoutes';
import { useNavigate, generatePath } from 'react-router-dom';
import { AppRoutes } from 'routes/routeConstants/appRoutes';
import { deserialize, serialize } from 'serializr';
import { Product } from 'models/Product/product.model';
import { NewProduct } from 'models/NewProduct/NewProduct.model';
import { ChildProduct } from 'models/ChildProduct/childProduct.model';
import { AxiosError } from 'axios';
import { pageCountOne } from 'shared/components/COProductCard/constants';
import { fetchFileAsRcFile } from 'shared/utils/getFileName';
import useRedirect from 'shared/utils/useRedirect';
import { RcFile } from 'antd/lib/upload';
import { ProductServiceProps } from 'enums/ProductServiceEnums';

export interface ProductCreationUpdationType {
  name: string;
  description: string;
  brand_id: number | undefined;
  net_weight: number | null;
  net_weight_unit: string | undefined;
  gross_weight: number | null;
  temperature_control: string | undefined;
  density: number | null;
  processing_facility_id: number | undefined;
  reporting_period_units: number | null;
  reporting_year: number | null;
  processing_facility_cost_percentage: number | null;
  id?: string;
  unique_id: string | null;
}

const ProductCreationService = () => {
  const navigate = useNavigate();
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [productLoading, setProductLoading] = useState<boolean>(false);
  const [productOverviewLoader, setProductOverviewLoader] = useState<boolean>(false);
  const [newProduct, setNewProduct] = useState<Product>();
  const [pageSize, setPageSize] = useState<number>(20);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [productList, setProductList] = useState<NewProduct[]>([]);
  const [childProductList, setChildProductList] = useState<ChildProduct[]>([]);
  const [productImage, setProductImage] = useState<RcFile>();
  const { redirectToProductListing } = useRedirect();

  const createProduct = async (payload: ProductCreationUpdationType) => {
    try {
      setButtonLoading(true);
      const modifiedPayload = { product: { ...payload } };
      const response = await axiosInstance.post(
        ApiRoutes.CREATE_PRODUCT,
        modifiedPayload
      );
      const id = response.data['data']['product']['id'];
      return id;
    } catch (error) {
      const axiosError = error as AxiosError;
      const isPfError = axiosError?.response?.data?.error_code;
      throw isPfError;
    } finally {
      setButtonLoading(false);
    }
  };


  /** Update Product */
  const updateProduct = async (payload: ProductCreationUpdationType) => {
    try {
      setButtonLoading(true);
      const url = generatePath(ApiRoutes.FETCH_PRODUCT_BY_ID, {
        id: payload.id,
      });
      const modifiedPayload = { product: { ...payload } };
      const response = await axiosInstance.put(url, modifiedPayload);
      const id = response.data['data']['product']['id'];
      return id;
    } catch (error) {
      const axiosError = error as AxiosError;
      const isPfError = axiosError?.response?.data?.error_code;
      throw isPfError;
    } finally {
      setButtonLoading(false);
    }
  };

  const uploadProductImage = async (productId: string, fileObj: RcFile, isCommodity?: boolean, isLiveStock?: boolean) => {
    try {
      setButtonLoading(true);
      const apiURL = isCommodity ? ApiRoutes.COMMODITY_IMAGE : isLiveStock ? ApiRoutes.LIVE_STOCK_COMMODITY_IMAGE : ApiRoutes.PRODUCT_IMAGE
      const url = generatePath(apiURL, {
        id: productId,
      });
      var formData = new FormData();
      formData.append('file', fileObj);
      await axiosInstance.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
    } finally {
      setButtonLoading(false);
    }
  };

  /** Fetch Single Brand */
  const fetchSingleProduct = async (id: number, fetchImageFile?: boolean) => {
    try {
      setProductLoading(true);
      setProductOverviewLoader(true);
      const url = generatePath(ApiRoutes.FETCH_PRODUCT_BY_ID, { id });

      const response = await axiosInstance.get(url);

      const data = deserialize(
        Product,
        response.data['data']['product']
      ) as Product;
      if (fetchImageFile && data?.image) {
        const image = await fetchFileAsRcFile(data?.image);
        setProductImage(image);
      }
      setNewProduct(data);
      return data;
    } catch (error) {
    } finally {
      setProductLoading(false);
    }
  };

  /** Fetch Products List*/
  const fetchProducts = async (params?: {
    page?: number;
    size?: number;
    status?: string;
    search?: string;
  }) => {
    try {
      setLoading(true);
      if (!params?.size) {
        params!.size = 20;
      }
      const response = await axiosInstance.get(ApiRoutes.FETCH_PRODUCTS, {
        params,
      });

      const data = deserialize(
        NewProduct,
        response.data['data'] as NewProduct[]
      );
      setProductList(data);
      setTotalPages(response.data[ProductServiceProps.TOTAL_PAGES]);
      setTotalItems(response.data[ProductServiceProps.TOTAL_ITEMS]);
      setCurrentPage(response.data[ProductServiceProps.CURRENT_PAGE]);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchChildProducts = async (
    name?: string,
    page: number = pageCountOne
  ) => {
    try {
      setLoading(true);
      const params = {
        product_name: name,
        page,
      };
      const response = await axiosInstance.get(ApiRoutes.CHILD_PRODUCTS, {
        params,
      });

      const data = deserialize(
        ChildProduct,
        response.data.products as ChildProduct[]
      );
      if (response.data.products.length === 0) {
        redirectToProductListing();
      }

      setChildProductList(data);
      setTotalPages(response.data.meta[ProductServiceProps.TOTAL_PAGES]);
      setTotalItems(response.data.meta[ProductServiceProps.TOTAL_ITEMS]);
      setCurrentPage(response.data.meta[ProductServiceProps.CURRENT_PAGE]);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  /** Handle delete product */
  const deleteProduct = async (id: number) => {
    try {
      setLoading(true);
      const url = generatePath(ApiRoutes.DELETE_PRODUCT, { id });
      const response = await axiosInstance.delete(url);
      const newArray = productList.filter(element => element.commodityId !== id);
      const childArray = childProductList.filter(element => element.commodityId !== id);
      setProductList(newArray);
      setChildProductList(childArray);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  /** Handle Duplicate product */
  const duplicateProduct = async (productId: number) => {
    try {
      setLoading(true);
      const url = generatePath(ApiRoutes.DUPLICATE_PRODUCT, { id: productId });
      const res = await axiosInstance.post(url);
      navigate(`${AppRoutes.PRODUCT}/${AppRoutes.DRAFT}`);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return {
    buttonLoading,
    loading,
    pageSize,
    totalItems,
    totalPages,
    currentPage,
    newProduct,
    setNewProduct,
    productLoading,
    setPageSize,
    createProduct,
    fetchSingleProduct,
    fetchProducts,
    deleteProduct,
    duplicateProduct,
    productList,
    uploadProductImage,
    updateProduct,
    productOverviewLoader,
    setProductOverviewLoader,
    childProductList,
    fetchChildProducts,
    productImage,
  };
};

export default ProductCreationService; 